import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import supabase from "@/lib/supabase";
import { isRecoveryFragment } from "@/lib/recoveryLink";

const AuthContext = createContext(null);

function buildProfile(authUser) {
  if (!authUser) return null;
  const meta = authUser.user_metadata || {};
  return {
    user_id: authUser.id,
    email: authUser.email,
    name: meta.name || meta.full_name || "",
    picture: meta.picture || meta.avatar_url || "",
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback((authUser) => {
    const profile = buildProfile(authUser);
    setUser(profile);
    return profile;
  }, []);

  useEffect(() => {
    const init = async () => {
      // 1. Check if auth tokens were passed in URL (from mobile app WebView)
      const urlParams = new URLSearchParams(window.location.search);
      const at = urlParams.get("at");
      const rt = urlParams.get("rt");
      const isAppEmbed = urlParams.get("app") === "1";

      if (at && rt) {
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: at,
            refresh_token: rt,
          });
          if (data?.session?.user && !error) {
            refreshUser(data.session.user);
            setLoading(false);

            // When running inside the mobile app's iframe, keep the tokens
            // in the URL so the session can be re-established if storage
            // partitioning causes it to be lost. In a normal browser tab,
            // clean them to avoid leaking tokens in the address bar.
            if (!isAppEmbed) {
              urlParams.delete("at");
              urlParams.delete("rt");
              const newSearch = urlParams.toString();
              const newUrl = window.location.pathname + (newSearch ? "?" + newSearch : "") + window.location.hash;
              window.history.replaceState(null, "", newUrl);
            }
            return;
          }
        } catch (e) {
          console.warn("Set session from URL params error:", e);
        }
      }

      // 2. Fallback: try the hash fragment (Supabase OAuth / mobile SSO)
      //
      // Recovery links are deliberately excluded. Their fragment looks identical
      // to an OAuth return, so this branch used to sign the user in and discard
      // the token — which is why a reset link appeared to "just log me in" and
      // left no way to choose a new password. ResetPassword owns that exchange.
      if (window.location.hash && !isRecoveryFragment(window.location.hash)) {
        try {
          const hashParams = new URLSearchParams(window.location.hash.slice(1));
          const hashAt = hashParams.get("access_token");
          const hashRt = hashParams.get("refresh_token");
          if (hashAt && hashRt) {
            const { data, error } = await supabase.auth.setSession({
              access_token: hashAt,
              refresh_token: hashRt,
            });
            if (data?.session?.user && !error) {
              refreshUser(data.session.user);
              setLoading(false);
              return;
            }
          }
        } catch (e) {
          console.warn("Set session from hash error:", e);
        }
      }

      // 3. Fallback to normal session get from storage
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          refreshUser(session.user);
        }
      } catch (e) {
        console.warn("getSession error:", e);
      }
      setLoading(false);
    };
    init();

    // 3. Listen for postMessage from parent WebView
    const messageHandler = async (event) => {
      if (event.data && (event.data.type === "WEGATHERED_AUTH" || event.data.type === "WEGATHERED_SET_SESSION")) {
        const at = event.data.access_token;
        const rt = event.data.refresh_token;
        if (at && rt) {
          try {
            const { data } = await supabase.auth.setSession({ access_token: at, refresh_token: rt });
            if (data?.session?.user) {
              refreshUser(data.session.user);
            }
          } catch (e) {
            console.warn("Set session from message error:", e);
          }
        }
      }
    };
    window.addEventListener("message", messageHandler);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        refreshUser(session.user);
      } else if (event === "SIGNED_OUT") {
        setUser(null);
      }
    });

    return () => {
      window.removeEventListener("message", messageHandler);
      subscription.unsubscribe();
    };
  }, [refreshUser]);

  const signin = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return refreshUser(data.user);
  };

  const signup = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    if (data.user && !data.session) {
      return { requires_confirmation: true, email };
    }
    if (data.session) {
      refreshUser(data.user);
    }
    return { requires_confirmation: false };
  };

  const resendConfirmation = async (email) => {
    const { error } = await supabase.auth.resend({ type: "signup", email });
    if (error) throw error;
    return { message: "Confirmation email sent" };
  };

  const forgotPassword = async (email) => {
    // Without redirectTo, Supabase falls back to the project's Site URL, which
    // sent people to the landing page with a recovery token they could not use.
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    if (error) throw error;
    return { message: "Reset email sent" };
  };

  const updateProfile = async (name, picture) => {
    const { data, error } = await supabase.auth.updateUser({
      data: { name, picture }
    });
    if (error) throw error;
    const profile = refreshUser(data.user);
    return profile;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    window.location.href = "/";
  };

  const login = () => {
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, signin, signup, updateProfile, resendConfirmation, forgotPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
